import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMediaQuery } from '@mantine/hooks'
import { Text, Box, Stack, Card, Loader, Timeline, SimpleGrid, Button, Group, NativeSelect } from '@mantine/core'
import { format, formatDistanceToNow } from 'date-fns'
import { IoBookmark } from 'react-icons/io5'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { Profile } from '../../app/models/profile'
import { Post } from '../../app/models/post'
import { Part } from '../../app/models/part'
import { FirearmBuild } from '../../app/models/build'
import type { UserFirearmDto } from '../../app/models/gunVault'
import { PostCard, EmptyState } from '../../app/common/ui'
import ProfilePhotos from './ProfilePhotos'
import FollowList from './FollowList'
import BuildCardThumbnail from '../builds/BuildCardThumbnail'

const TIER_THRESHOLDS: Array<{ name: string; min: number; color: string }> = [
  { name: 'Newcomer', min: 0, color: '#8B949E' },
  { name: 'Enthusiast', min: 10, color: '#58A6FF' },
  { name: 'Contributor', min: 50, color: '#3FB950' },
  { name: 'Expert', min: 100, color: '#C9A84C' },
  { name: 'Curator', min: 200, color: '#E85D04' },
  { name: 'Armorer', min: 500, color: '#F85149' }
]

function getTierInfo (score: number) {
  let current = TIER_THRESHOLDS[0]
  let next: typeof TIER_THRESHOLDS[0] | null = null
  for (let i = 0; i < TIER_THRESHOLDS.length; i++) {
    if (score >= TIER_THRESHOLDS[i].min) {
      current = TIER_THRESHOLDS[i]
      next = TIER_THRESHOLDS[i + 1] ?? null
    }
  }
  const progress = next
    ? Math.min(100, Math.round(((score - current.min) / (next.min - current.min)) * 100))
    : 100
  return { current, next, progress }
}

interface Props {
  profile: Profile;
  canEdit?: boolean;
}

function actionLabel (action: string): string {
  if (action === 'AdjustReputation') return 'Reputation adjustment'
  return action.replace(/([A-Z])/g, ' $1').trim() || action
}

// Read-only vault card for profile view
function VaultCardReadOnly ({ item }: { item: UserFirearmDto }) {
  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      sx={{
        border: item.isPinned ? '2px solid var(--gs-gold)' : '1px solid var(--gs-border)',
        background: 'var(--gs-surface)',
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,.15)' }
      }}
    >
      {item.coverImageUrl && (
        <Card.Section mb="xs">
          <img
            src={item.coverImageUrl}
            alt={`${item.make} ${item.model}`}
            style={{ width: '100%', height: 100, objectFit: 'cover' }}
          />
        </Card.Section>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Text
            weight={600}
            size="sm"
            lineClamp={1}
            component={Link}
            to={`/registry/${item.firearmModelId}`}
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'var(--gs-accent)' } }}
          >
            {item.make} {item.model}
          </Text>
          {item.caliber && (
            <Text size="xs" color="dimmed" mt={2}>{item.caliber}</Text>
          )}
        </Box>
        {item.isPinned && (
          <IoBookmark size={13} color="var(--gs-gold)" style={{ flexShrink: 0, marginLeft: 6 }} />
        )}
      </Box>
    </Card>
  )
}

export default observer(function ProfileContent ({ profile, canEdit }: Props) {
  const { profileStore } = useStore()
  const mobileTabs = useMediaQuery('(max-width: 768px)') ?? false
  const [activeTab, setActiveTab] = useState<string>('posts')
  const [posts, setPosts] = useState<Post[]>([])
  const [reviews, setReviews] = useState<Post[]>([])
  const [builds, setBuilds] = useState<FirearmBuild[]>([])
  const [parts, setParts] = useState<Part[]>([])
  const [vault, setVault] = useState<UserFirearmDto[] | null>(null)
  const [loadingPosts, setLoadingPosts] = useState(false)
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [loadingBuilds, setLoadingBuilds] = useState(false)
  const [loadingParts, setLoadingParts] = useState(false)
  const [loadingVault, setLoadingVault] = useState(false)

  useEffect(() => {
    if (!profile.username) return
    setLoadingPosts(true)
    agent.Posts.list({ authorUsername: profile.username, pageSize: 20 })
      .then((r) => setPosts(r.items))
      .catch(() => setPosts([]))
      .finally(() => setLoadingPosts(false))
  }, [profile.username])

  const loadReviews = () => {
    if (reviews.length > 0) return
    setLoadingReviews(true)
    agent.Posts.list({ authorUsername: profile.username, type: 1, pageSize: 20 })
      .then((r) => setReviews(r.items))
      .catch(() => setReviews([]))
      .finally(() => setLoadingReviews(false))
  }

  const loadBuilds = () => {
    if (builds.length > 0) return
    setLoadingBuilds(true)
    agent.Builds.list({ ownerUsername: profile.username, pageSize: 20 })
      .then((r) => setBuilds(r.items))
      .catch(() => setBuilds([]))
      .finally(() => setLoadingBuilds(false))
  }

  const loadParts = () => {
    if (parts.length > 0) return
    setLoadingParts(true)
    agent.Parts.list({ authorUsername: profile.username, pageSize: 20 })
      .then((r) => setParts(r.items))
      .catch(() => setParts([]))
      .finally(() => setLoadingParts(false))
  }

  const loadVault = () => {
    if (vault !== null) return
    setLoadingVault(true)
    agent.Profiles.getPublicVault(profile.username)
      .then((items) => setVault(items))
      .catch(() => setVault([]))
      .finally(() => setLoadingVault(false))
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    if (value === 'reviews') loadReviews()
    if (value === 'builds') loadBuilds()
    if (value === 'parts') loadParts()
    if (value === 'vault') loadVault()
    if (value === 'activity' && profile.username) profileStore.loadActivityFeed(profile.username)
  }

  const mapPostToCard = (p: Post) => (
    <PostCard
      key={p.id}
      id={p.id}
      title={p.title}
      excerpt={p.excerpt}
      type={p.type}
      coverImageUrl={p.coverImageUrl}
      mainPhotoUrl={p.mainPhotoUrl}
      authorUsername={p.authorUsername}
      authorDisplayName={p.authorDisplayName}
      authorImage={p.authorImage}
      authorTier={p.authorAmmoTier}
      authorScore={p.authorAmmoScore}
      voteScore={p.voteScore ?? 0}
      currentUserVote={p.currentUserVote}
      commentCount={p.comments?.length ?? 0}
      createdAt={p.createdAt}
      tags={p.tags}
      variant="default"
    />
  )

  const tabs = [
    'posts',
    'reviews',
    'builds',
    'parts',
    'vault',
    'followers',
    'following',
    'about',
    'photos',
    ...(canEdit ? ['activity'] : [])
  ]

  const tabStyle = (value: string) => ({
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 'clamp(11px, 2.2vw, 13px)',
    color: activeTab === value ? 'var(--gs-accent)' : 'var(--gs-muted)',
    borderBottom: activeTab === value ? '2px solid var(--gs-accent)' : '2px solid transparent',
    padding: mobileTabs ? '8px 6px' : '10px 12px',
    marginBottom: -1,
    cursor: 'pointer' as const,
    background: 'none',
    border: 'none',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0
  })

  return (
    <Box>
      {mobileTabs
        ? (
        <Box
          sx={{
            boxSizing: 'border-box',
            background: 'var(--gs-surface)',
            borderBottom: '1px solid var(--gs-border)',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 10,
            paddingBottom: 10
          }}
        >
          <NativeSelect
            value={activeTab}
            onChange={(e) => handleTabChange(e.currentTarget.value)}
            data={tabs.map((t) => ({
              value: t,
              label: t.charAt(0).toUpperCase() + t.slice(1)
            }))}
            styles={{
              input: {
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 500,
                borderColor: 'var(--gs-border)',
                backgroundColor: 'var(--gs-bg)'
              }
            }}
          />
        </Box>
          )
        : (
        <Box
          sx={{
            boxSizing: 'border-box',
            background: 'var(--gs-surface)',
            borderBottom: '1px solid var(--gs-border)',
            paddingLeft: 16,
            paddingRight: 16,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 0,
            rowGap: 2
          }}
        >
          {tabs.map((value) => (
            <Box
              key={value}
              component="button"
              type="button"
              onClick={() => handleTabChange(value)}
              sx={tabStyle(value)}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </Box>
          ))}
        </Box>
          )}

      {/* Content area */}
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr 280px',
          gap: 16,
          paddingTop: 16,
          paddingBottom: 16,
          paddingLeft: 16,
          paddingRight: 16,
          '@media (max-width: 800px)': {
            gridTemplateColumns: '1fr'
          }
        }}
      >
        <Box>
          {activeTab === 'posts' && (
            <>
              {loadingPosts
                ? <Loader size="sm" />
                : posts.length === 0
                  ? (
                <Box sx={{ padding: '32px 0', textAlign: 'center' }}>
                  <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, color: 'var(--gs-muted)', letterSpacing: 0.5, marginBottom: 6 }}>
                    Nothing posted yet
                  </Text>
                  <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', marginBottom: canEdit ? 16 : 0 }}>
                    {canEdit ? 'Share your knowledge, reviews, or tips with the community.' : "This member hasn't posted yet."}
                  </Text>
                  {canEdit && (
                    <Button component={Link} to="/create-post" size="xs" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
                      Write a post
                    </Button>
                  )}
                </Box>
                    )
                  : <Stack spacing="sm">{posts.map(mapPostToCard)}</Stack>}
            </>
          )}

          {activeTab === 'reviews' && (
            <>
              {loadingReviews
                ? <Loader size="sm" />
                : reviews.length === 0
                  ? (
                <Box sx={{ padding: '32px 0', textAlign: 'center' }}>
                  <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, color: 'var(--gs-muted)', letterSpacing: 0.5, marginBottom: 6 }}>
                    No reviews yet
                  </Text>
                  <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', marginBottom: canEdit ? 16 : 0 }}>
                    {canEdit ? 'Tried a firearm or accessory? Share what you think.' : 'No reviews from this member yet.'}
                  </Text>
                  {canEdit && (
                    <Button component={Link} to="/create-post?type=1" size="xs" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
                      Write a review
                    </Button>
                  )}
                </Box>
                    )
                  : <Stack spacing="sm">{reviews.map(mapPostToCard)}</Stack>}
            </>
          )}

          {activeTab === 'builds' && (
            <>
              {loadingBuilds
                ? <Loader size="sm" />
                : builds.length === 0
                  ? (
                <Box sx={{ padding: '32px 0', textAlign: 'center' }}>
                  <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, color: 'var(--gs-muted)', letterSpacing: 0.5, marginBottom: 6 }}>
                    No documented builds
                  </Text>
                  <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', marginBottom: canEdit ? 16 : 0 }}>
                    {canEdit ? 'Document your builds and earn AmmoScore when the community votes them up.' : 'No builds shared yet.'}
                  </Text>
                  {canEdit && (
                    <Button component={Link} to="/builds/new" size="xs" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
                      Create a build
                    </Button>
                  )}
                </Box>
                    )
                  : (
                  <Stack spacing="sm">
                    {builds.map((b) => (
                      <Card
                        key={b.id}
                        component={Link}
                        to={`/builds/${b.id}`}
                        padding="sm"
                        sx={{ textDecoration: 'none', color: 'inherit', background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}
                      >
                        <Group align="flex-start" spacing="sm" noWrap>
                          <BuildCardThumbnail url={b.mainPhotoUrl} alt={b.name || ''} variant="compact" />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Text weight={500} sx={{ fontFamily: 'Oswald, sans-serif' }}>{b.name || b.firearmModelName || 'Build'}</Text>
                            <Text size="xs" color="dimmed" mt={4}>
                              {b.firearmModelName ?? (b.firearmModelId ? 'Model pending review' : '—')} · {b.parts?.length ?? 0} parts · {b.voteScore ?? 0} votes · {format(new Date(b.updatedAt), 'MMM d, yyyy')}
                            </Text>
                          </Box>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                    )}
            </>
          )}

          {activeTab === 'parts' && (
            <>
              {loadingParts
                ? <Loader size="sm" />
                : parts.length === 0
                  ? (
                <Box sx={{ padding: '32px 0', textAlign: 'center' }}>
                  <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, color: 'var(--gs-muted)', letterSpacing: 0.5, marginBottom: 6 }}>
                    No parts contributed
                  </Text>
                  <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', marginBottom: canEdit ? 16 : 0 }}>
                    {canEdit ? 'Add parts to your bin and the community can upvote them for AmmoScore.' : 'No parts from this member yet.'}
                  </Text>
                  {canEdit && (
                    <Button component={Link} to="/parts-bin" size="xs" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
                      Head to Parts Bin
                    </Button>
                  )}
                </Box>
                    )
                  : (
                <Stack spacing="sm">
                  {parts.map((p) => (
                    <Card
                      key={p.id}
                      component={Link}
                      to="/parts-bin"
                      padding="sm"
                      sx={{ textDecoration: 'none', color: 'inherit', background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}
                    >
                      <Text weight={500} size="sm">{p.name}</Text>
                      <Text size="xs" color="dimmed">{p.manufacturer} · {format(new Date(p.createdAt), 'MMM d, yyyy')}</Text>
                    </Card>
                  ))}
                </Stack>
                    )}
            </>
          )}

          {activeTab === 'vault' && (
            <>
              {loadingVault
                ? (
                <Loader size="sm" />
                  )
                : vault === null
                  ? null
                  : vault.length === 0
                    ? (
                <Box sx={{ padding: '32px 0', textAlign: 'center' }}>
                  <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, color: 'var(--gs-muted)', letterSpacing: 0.5, marginBottom: 6 }}>
                    Vault is private or empty
                  </Text>
                  <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)', marginBottom: canEdit ? 16 : 0 }}>
                    {canEdit ? 'Your vault is where you keep your firearms collection. Add your first gun.' : "This member's vault isn't public."}
                  </Text>
                  {canEdit && (
                    <Button component={Link} to="/gun-vault" size="xs" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
                      Go to your Vault
                    </Button>
                  )}
                </Box>
                      )
                    : (
                <SimpleGrid
                  cols={1}
                  breakpoints={[{ minWidth: 480, cols: 2 }, { minWidth: 720, cols: 3 }]}
                  spacing="sm"
                >
                  {vault.map((item) => (
                    <VaultCardReadOnly key={item.id} item={item} />
                  ))}
                </SimpleGrid>
                      )}
            </>
          )}

          {activeTab === 'followers' && (
            <FollowList key={`followers-${profile.username}`} username={profile.username} mode="followers" />
          )}

          {activeTab === 'following' && (
            <FollowList key={`following-${profile.username}`} username={profile.username} mode="following" />
          )}

          {activeTab === 'about' && (
            <Box sx={{ padding: '8px 0' }}>
              {profile.bio && (
                <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-text)', whiteSpace: 'pre-wrap' }}>
                  {profile.bio}
                </Text>
              )}
              <Box mt="md" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
                <Text>Total votes received: {profile.totalVotesReceived ?? 0}</Text>
                <Text>Approved submissions: {profile.approvedSubmissionsCount ?? 0}</Text>
              </Box>
            </Box>
          )}

          {activeTab === 'photos' && (
            <Box sx={{ padding: '8px 0' }}>
              <ProfilePhotos profile={profile} />
            </Box>
          )}

          {activeTab === 'activity' && canEdit && (
            <>
              {profileStore.loadingFeed
                ? <Loader size="sm" />
                : !profileStore.activityFeed?.items?.length
                    ? (
                <EmptyState title="No activity yet" message="Reputation adjustments will appear here" />
                      )
                    : (
                <Timeline bulletSize={24} lineWidth={2} color="orange" mt="md">
                  {profileStore.activityFeed.items.map((item) => (
                    <Timeline.Item key={item.id}>
                      <Text weight={600} size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)' }}>{actionLabel(item.action)}</Text>
                      <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>{item.detail}</Text>
                      <Text size="xs" sx={{ color: 'var(--gs-muted)', marginTop: 4 }}>
                        {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                        {item.scoreDelta != null && (
                          <Text component="span" size="xs" weight={500} ml="xs" sx={{ color: item.scoreDelta >= 0 ? '#3FB950' : '#F85149' }}>
                            {item.scoreDelta >= 0 ? `+${item.scoreDelta}` : item.scoreDelta}
                          </Text>
                        )}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
                      )}
            </>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16, '@media (max-width: 800px)': { display: 'none' } }}>
          {/* AmmoScore tier card */}
          {(() => {
            const score = profile.ammoScore ?? 0
            const { current, next, progress } = getTierInfo(score)
            return (
              <Card padding="md" sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <Text weight={600} size="xs" sx={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--gs-muted)' }}>
                    AmmoScore
                  </Text>
                  <Box sx={{
                    background: `${current.color}22`,
                    border: `1px solid ${current.color}66`,
                    color: current.color,
                    padding: '2px 8px',
                    borderRadius: 3,
                    fontSize: 10,
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase'
                  }}>
                    {current.name}
                  </Box>
                </Box>

                <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 28, color: current.color, lineHeight: 1, marginBottom: 8 }}>
                  {score}
                </Text>

                {/* Progress bar */}
                <Box sx={{ marginBottom: 6 }}>
                  <Box sx={{ background: 'var(--gs-surface2)', borderRadius: 3, height: 5, overflow: 'hidden' }}>
                    <Box sx={{
                      width: `${progress}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${current.color}99, ${current.color})`,
                      borderRadius: 3,
                      transition: 'width 0.4s ease'
                    }} />
                  </Box>
                  {next
                    ? (
                    <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)', marginTop: 4 }}>
                      {next.min - score} more to reach{' '}
                      <Text component="span" sx={{ color: next.color, fontWeight: 600 }}>{next.name}</Text>
                    </Text>
                      )
                    : (
                    <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-gold)', marginTop: 4, fontWeight: 600 }}>
                      Maximum rank achieved
                    </Text>
                      )}
                </Box>

                <Box sx={{ borderTop: '1px solid var(--gs-border)', marginTop: 10, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>Post votes</Text>
                    <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: '#3FB950', fontWeight: 600 }}>+{profile.totalVotesReceived ?? 0}</Text>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>Parts approved</Text>
                    <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: '#3FB950', fontWeight: 600 }}>+{profile.approvedSubmissionsCount ?? 0}</Text>
                  </Box>
                </Box>
              </Card>
            )
          })()}

          {canEdit && (
            <Card padding="md" sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}>
              <Text weight={600} size="xs" sx={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--gs-muted)', marginBottom: 12 }}>
                Quick Actions
              </Text>
              <Stack spacing="xs">
                <Text component={Link} to={`/profiles/${profile.username}/edit`} size="sm" sx={{ color: 'var(--gs-accent)' }}>
                  Edit Profile
                </Text>
                <Text
                  component="button"
                  type="button"
                  size="sm"
                  sx={{ color: 'var(--gs-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  onClick={() => handleTabChange('photos')}
                >
                  Upload Photo
                </Text>
                <Text
                  component="button"
                  type="button"
                  size="sm"
                  sx={{ color: 'var(--gs-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  onClick={() => handleTabChange('activity')}
                >
                  View Activity
                </Text>
                <Text
                  component={Link}
                  to="/gun-vault"
                  size="sm"
                  sx={{ color: 'var(--gs-accent)' }}
                >
                  Manage Vault
                </Text>
              </Stack>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  )
})
